import React, {useContext, useEffect, useCallback, useState, useRef} from 'react';
import {Context} from '../contexts/WordList';
import {useSearchWords} from '../effects/useSearchWords';
import {FilterList as FilterListComponent} from '../components/FilterList';
import {Link} from 'react-router-dom';
import {AI_FEATURES_ENABLED} from '../helpers/featureFlags';
import {useAiAvailable} from '../contexts/AiAvailability';
import {useAuth} from '../contexts/Auth';
import {availableTagEntries} from '../helpers/availableTagEntries';

const _ = require('lodash');


export const Topbar = ({location}) => {
  const [state, dispatch] = useContext(Context);
  const {language, orderBy, tagsFilter} = state;
  const [displayFilterList, setDisplayFilterList] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileRef = useRef(null);
  const searchInputRef = useRef(null);
  const {searchWords} = useSearchWords();
  const {aiAvailable, aiChecking} = useAiAvailable();
  const {user, logout} = useAuth();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getDisplayName = () => {
    if (user && user.name) return user.name;
    if (user && user.email) {
      return user.email.split('@')[0];
    }
    return 'User';
  };

  const handleLogout = async () => {
    setShowProfileMenu(false);
    await logout();
  };

  useEffect(() => {
    // console.log('************ topbar ******************');
  }, []);

  const handleLanguageChange = (e) => {
    const newlang = e.target.value;

    // Reset any active search so stale queries from the previous
    // language are not re-applied after the switch.
    debounce.cancel();
    setSearchQuery('');
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }

    dispatch({
      type: 'HAS_MORE_ITEMS',
      payload: false,
    });
    dispatch({
      type: 'EMPTY_LIST',
      payload: '',
    });
    dispatch({
      type: 'SET_LAST_WORD',
      payload: '',
    });
    dispatch({
      type: 'CHANGE_LANGUAGE',
      payload: newlang,
    });
    dispatch({
      type: 'HAS_MORE_ITEMS',
      payload: true,
    });
  };


  const [searchQuery, setSearchQuery] = useState('');

  const debounce = useCallback(
      _.debounce((searchWord) => {
        setSearchQuery(searchWord);
        if (searchWord !== '') {
          dispatch({
            type: 'HAS_MORE_ITEMS',
            payload: false,
          });
          dispatch({
            type: 'EMPTY_LIST',
            payload: '',
          });

          searchWords(searchWord);
        } else {
          dispatch({
            type: 'SET_LAST_WORD',
            payload: '',
          });
          dispatch({
            type: 'EMPTY_LIST',
            payload: '',
          });

          dispatch({
            type: 'HAS_MORE_ITEMS',
            payload: true,
          });
        }
      }, 1000),
      [orderBy, language],
  );

  // Cancel pending debounced searches on unmount.
  useEffect(() => () => debounce.cancel(), [debounce]);

  // When the sort field changes while a search is active, re-run the
  // search so results stay sorted by the newly selected field.
  const prevOrderByRef = useRef(orderBy);
  useEffect(() => {
    if (prevOrderByRef.current === orderBy) return;
    prevOrderByRef.current = orderBy;
    if (searchQuery) {
      searchWords(searchQuery);
    }
  }, [orderBy, searchQuery, searchWords]);

  const handleSearch = (e) => {
    debounce(e.target.value.trim());
  };

  const handleSortToggle = () => {
    // Apply any pending debounced search first so the re-search effect
    // below sees the latest query.
    debounce.flush();
    dispatch({
      type: 'SORT_WORDS',
      payload: orderBy === 'wordEn' ? 'wordFa' : 'wordEn',
    });
  };

  const targetLangLabel = language === 'hebrew' ? 'Hebrew' : 'Farsi';
  const sortToggleLabel = orderBy === 'wordEn' ?
    'EN' :
    (language === 'hebrew' ? 'HE' : 'FA');

  // The badge counts filters that hide words (unchecked tags), so it
  // only appears when the list is actually filtered.
  const hiddenFiltersCount = availableTagEntries
      .filter((tag) => tag !== 'Add' && !tagsFilter.includes(tag))
      .length;


  // const getcurrLang = () => {
  //   console.log({orderBy: orderBy, language: language});
  //   return {orderBy: orderBy, language: language};
  // };

  const handleDisplayFilterList = () => {
    setDisplayFilterList(true);
  };

  const handleCloseFilterList = () => {
    setDisplayFilterList(false);
  };

  const isWordsMode = location.pathname === '/';

  useEffect(() => {
    if (!isWordsMode) {
      setDisplayFilterList(false);
    }
  }, [isWordsMode]);

  return (
    <>
      <header className="topbar">
        <div className="topbar-row">
          <div className="topbar-title">VocabMaster</div>

          {/* Desktop nav tabs - hidden on mobile (bottom tab bar takes over) */}
          <nav className="nav-tabs">
            <Link to="/" className={`nav-tab${location.pathname === '/' ? ' active' : ''}`}>
              Words
            </Link>
            <Link to="/study" className={`nav-tab${location.pathname === '/study' ? ' active' : ''}`}>
              Study
            </Link>
            <Link to="/progress" className={`nav-tab${location.pathname === '/progress' ? ' active' : ''}`}>
              Progress
            </Link>
            {AI_FEATURES_ENABLED && (
              <Link to="/practice" className={`nav-tab${location.pathname === '/practice' ? ' active' : ''}`}>
                Practice{!aiChecking && !aiAvailable && <span style={{display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: '#ff3b30', marginLeft: 5, verticalAlign: 'middle'}} title="AI service offline" />}
              </Link>
            )}
            {language === 'hebrew' && (
              <Link to="/verbs" className={`nav-tab${location.pathname === '/verbs' ? ' active' : ''}`}>
                Verbs
              </Link>
            )}
          </nav>

          {isWordsMode ? (
            <>
              <div className="topbar-search-row">
                <input
                  ref={searchInputRef}
                  className="topbar-search"
                  placeholder="🔍 Search both languages..."
                  onChange={handleSearch}
                  dir="auto"
                  aria-label="Search words in both languages"
                />

                <button
                  type="button"
                  className="icon-btn sort-toggle"
                  onClick={handleSortToggle}
                  aria-label={`Sorted by ${orderBy === 'wordEn' ? 'English' : targetLangLabel}. Tap to sort by ${orderBy === 'wordEn' ? targetLangLabel : 'English'}.`}
                  title={`Sort: ${orderBy === 'wordEn' ? 'English' : targetLangLabel}`}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M7 4v16" />
                    <path d="M4 16l3 4 3-4" />
                    <path d="M17 20V4" />
                    <path d="M14 8l3-4 3 4" />
                  </svg>
                  <span className="sort-toggle-label">{sortToggleLabel}</span>
                </button>
              </div>

              <button
                type="button"
                className="icon-btn filter-btn"
                onClick={handleDisplayFilterList}
                aria-label="Open filters"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="4" y1="6" x2="20" y2="6"/>
                  <line x1="8" y1="12" x2="16" y2="12"/>
                  <line x1="11" y1="18" x2="13" y2="18"/>
                </svg>
                {hiddenFiltersCount > 0 && (
                  <span className="filter-badge">{hiddenFiltersCount}</span>
                )}
              </button>
            </>
          ) : (
            <></>
          )}

          <div className="topbar-profile" ref={profileRef}>
            <button
              type="button"
              className="topbar-profile-btn"
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              aria-label="Profile menu"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </button>
            {showProfileMenu && (
              <div className="topbar-profile-dropdown">
                <div className="topbar-profile-info">
                  <span className="topbar-profile-name">{getDisplayName()}</span>
                  {user && user.email &&
                    <span className="topbar-profile-email">
                      {user.email}
                    </span>
                  }
                </div>

                <div className="topbar-profile-section">
                  <span className="topbar-profile-section-label">Language</span>
                  <select
                    className="topbar-profile-select"
                    value={language}
                    onChange={handleLanguageChange}
                    aria-label="Language"
                  >
                    <option value="farsi">Farsi</option>
                    <option value="hebrew">Hebrew</option>
                  </select>
                </div>

                <div className="topbar-profile-divider" />
                <Link
                  to="/admin"
                  className={`topbar-profile-link${location.pathname === '/admin' ? ' active' : ''}`}
                  onClick={() => setShowProfileMenu(false)}
                >
                  Admin
                </Link>
                <div className="topbar-profile-divider" />
                <button
                  type="button"
                  className="topbar-profile-logout"
                  onClick={handleLogout}
                >
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <FilterListComponent
        displayFilterList={displayFilterList}
        onClose={handleCloseFilterList}
      />
    </>
  );
};

